import { Flex } from '@/layouts/Flex';
import { Colors } from '@/plugins/data/colors';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import styles from './styles.module.scss';

export interface IOption {
  key: string;
  label: string;
  description?: string;
  image?: string;
}

interface Props {
  options: IOption[];
  value: IOption[];
  onChange: (selected: IOption[]) => void;
  placeholder?: string;
  emptyMessage?: string;
}

const MIN_AMOUNT_FOR_SEARCH = 10;
const MAX_VISIBLE = 2;

export function DropdownMulti({
  options,
  value,
  onChange,
  placeholder = 'Selecciona opciones',
  emptyMessage = 'No se encontraron resultados'
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = search
    ? options.filter((opt) => {
        const term = search.toLowerCase();

        return opt.label.toLowerCase().includes(term) || (opt.description?.toLowerCase().includes(term) ?? false);
      })
    : options;

  const isSelected = (option: IOption) => {
    return value?.some((item) => item.key === option.key) ?? false;
  };

  const handleSelect = (option: IOption) => {
    const current = value ?? [];

    const exists = current.some((item) => item.key === option.key);

    const next = exists ? current.filter((item) => item.key !== option.key) : [...current, option];

    onChange(next.length ? next : []);
  };

  const resetSelection = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onChange([]);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || options.length <= MIN_AMOUNT_FOR_SEARCH) return;

    const id = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isOpen, options.length]);

  const renderSelected = () => {
    if ((value?.length ?? 0) === 0) {
      return (
        <Flex alignItems='center' justifyContent='space-between' style={{ height: '100%' }}>
          {placeholder}
          <Icon name='chevronDown' size={18} />
        </Flex>
      );
    }

    const visibleItems = value!.slice(0, MAX_VISIBLE);
    const hiddenCount = value!.length - MAX_VISIBLE;

    return (
      <div className={styles.dropdownSelectedMulti}>
        <div className={styles.dropdownTags}>
          {visibleItems.map((item) => (
            <span key={item.key} className={styles.dropdownTag}>
              {item.label}
            </span>
          ))}

          {hiddenCount > 0 && <span className={styles.dropdownTagMore}>+{hiddenCount}</span>}
        </div>

        <div onClick={resetSelection} className={styles.resetBtn} role='button' aria-label='Reset selection'>
          <Icon name='close' size={20} color={Colors.primaryDark} />
        </div>
      </div>
    );
  };

  if (!options.length) {
    return null;
  }

  return (
    <div className={styles.dropdown} ref={ref} role='combobox' aria-haspopup='listbox' aria-expanded={isOpen}>
      <div className={styles.dropdownHeader} onClick={() => setIsOpen((v) => !v)} role='button'>
        {renderSelected()}
      </div>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          {options.length > MIN_AMOUNT_FOR_SEARCH && (
            <input
              ref={searchRef}
              className={styles.dropdownSearch}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Buscar...'
            />
          )}

          {filteredOptions.length === 0 ? (
            <div className={styles.dropdownEmpty}>{search ? `No hay resultados para "${search}"` : emptyMessage}</div>
          ) : (
            filteredOptions.map((opt) => (
              <div
                key={opt.key}
                className={styles.dropdownItem}
                onClick={() => handleSelect(opt)}
                role='option'
                aria-selected={isSelected(opt)}>
                <div>
                  {opt.image && <img className={styles.dropdownImage} src={opt.image} loading='lazy' alt={opt.label} />}

                  <div>
                    <div className={styles.dropdownLabel}>{opt.label}</div>
                    {opt.description && <div className={styles.dropdownDescription}>{opt.description}</div>}
                  </div>
                </div>

                {isSelected(opt) && <Icon name='circleCheck' size={20} color={Colors.primary} />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
